import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .mc-page {
    min-height: 100vh;
    background: #0a1628;
    font-family: 'DM Sans', sans-serif;
    padding: 40px 20px 60px;
    color: #f0f4f8;
  }

  .mc-container {
    max-width: 680px;
    margin: 0 auto;
  }

  .mc-title {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .mc-card {
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.15);
    border-radius: 14px;
    padding: 28px;
    margin-bottom: 20px;
  }

  .mc-card__label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.35);
    margin-bottom: 16px;
  }

  .mc-avatar-row {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-bottom: 24px;
  }

  .mc-avatar {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    border: 2px solid rgba(0,173,181,0.4);
    object-fit: cover;
    flex-shrink: 0;
  }

  .mc-avatar-initial {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    border: 2px solid rgba(0,173,181,0.4);
    background: rgba(0,173,181,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 600;
    color: #00adb5;
    flex-shrink: 0;
  }

  .mc-avatar-info__name {
    font-size: 17px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 3px;
  }

  .mc-avatar-info__email {
    font-size: 13px;
    color: rgba(255,255,255,0.45);
  }

  .mc-field {
    margin-bottom: 16px;
  }

  .mc-field:last-child {
    margin-bottom: 0;
  }

  .mc-field__label {
    font-size: 12px;
    color: rgba(255,255,255,0.4);
    margin-bottom: 6px;
  }

  .mc-field__value {
    font-size: 14px;
    color: rgba(255,255,255,0.85);
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    padding: 10px 14px;
  }

  .mc-badge {
    display: inline-block;
    background: rgba(0,173,181,0.15);
    color: #00adb5;
    border: 1px solid rgba(0,173,181,0.3);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    padding: 3px 12px;
  }

  .mc-empty {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255,255,255,0.3);
    font-size: 15px;
  }
`;

export default function MinhaConta() {
  const [user, setUser] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Usuário';
  const initial     = (user?.displayName || user?.email || '?')[0].toUpperCase();

  const createdAt = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : '—';

  const lastLogin = user?.metadata?.lastSignInTime
    ? new Date(user.metadata.lastSignInTime).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'long', year: 'numeric',
      })
    : '—';

  return (
    <>
      <style>{styles}</style>
      <div className="mc-page">
        <div className="mc-container">
          <h1 className="mc-title">Minha Conta</h1>

          {!user ? (
            <div className="mc-empty">Você não está autenticado.</div>
          ) : (
            <>
              <div className="mc-card">
                <div className="mc-card__label">Perfil</div>
                <div className="mc-avatar-row">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={displayName} className="mc-avatar" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="mc-avatar-initial">{initial}</div>
                  )}
                  <div className="mc-avatar-info">
                    <div className="mc-avatar-info__name">{displayName}</div>
                    <div className="mc-avatar-info__email">{user.email}</div>
                  </div>
                </div>

                <div className="mc-field">
                  <div className="mc-field__label">E-mail</div>
                  <div className="mc-field__value">{user.email}</div>
                </div>

                <div className="mc-field">
                  <div className="mc-field__label">Status do e-mail</div>
                  <div className="mc-field__value">
                    <span className="mc-badge">
                      {user.emailVerified ? '✓ Verificado' : 'Não verificado'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mc-card">
                <div className="mc-card__label">Atividade</div>

                <div className="mc-field">
                  <div className="mc-field__label">Membro desde</div>
                  <div className="mc-field__value">{createdAt}</div>
                </div>

                <div className="mc-field">
                  <div className="mc-field__label">Último acesso</div>
                  <div className="mc-field__value">{lastLogin}</div>
                </div>

                <div className="mc-field">
                  <div className="mc-field__label">Provedor</div>
                  <div className="mc-field__value">
                    {user.providerData?.[0]?.providerId === 'google.com'
                      ? 'Google'
                      : user.providerData?.[0]?.providerId === 'password'
                      ? 'E-mail e senha'
                      : user.providerData?.[0]?.providerId || '—'}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
